'use client';

import { analiticaInstance } from '@payments-checkout/shared/plugins/analitica/analitica.events';
import { Checkbox, Divider } from '@stone-payments/jade';
import { useParams, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useFormTypedValues } from '../../app/providers/formValuesProvider.provider';
import { DeliveryCard } from '../deliveryCard/deliveryCard';
import { AddressFormComponent } from '../forms/address/addressFormComponent';
import { AddressHeader } from '../forms/address/addressHeader';
import { ShippingAddressReview } from '../shippingAddressReview/shippingAddressReview';

const BILLING_SCREEN = 'dados_cobranca';

const ADDRESS_FIELDS = [
  'street',
  'number',
  'complement',
  'district',
  'city',
  'state',
  'zip-code',
  'country',
  'without-number',
] as const;

export function DeliveryData({
  showAddress = false,
}: {
  showAddress?: boolean;
}) {
  const { getValues, setValue, control } = useFormContext();
  const { formatMessage } = useIntl();
  const params = useParams<{ paymentLinkId: string }>();
  const { push } = useRouter();
  const [sameAddress, setSameAddress] = useState(true);
  const { deliveryQuote } = useFormTypedValues();

  const billingCountry = useWatch({
    control,
    name: 'billing-address-country',
  }) as string | undefined;

  const shippingCountry = useWatch({
    control,
    name: 'shipping-address-country',
  }) as string | undefined;

  const copyShippingToBilling = () => {
    ADDRESS_FIELDS.forEach((field) => {
      setValue(
        `billing-address-${field}` as any,
        getValues(`shipping-address-${field}` as any)
      );
    });
  };

  useEffect(() => {
    copyShippingToBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSameAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;

    analiticaInstance.events.billingPage.sameAddressSelected(checked);
    setSameAddress(checked);
    if (checked) copyShippingToBilling();
  };

  const shippingAddress = {
    street: (getValues('shipping-address-street' as any) as string) ?? '',
    number: (getValues('shipping-address-number' as any) as string) ?? '',
    city: (getValues('shipping-address-city' as any) as string) ?? '',
    state: (getValues('shipping-address-state' as any) as string) ?? '',
    zip: (getValues('shipping-address-zip-code' as any) as string) ?? '',
    country: (getValues('shipping-address-country' as any) as string) ?? '',
  };

  return (
    <div className="flex flex-col gap-jade-200 min-500:gap-0">
      <div className="flex flex-col gap-jade-150 bg-jade-background-surface rounded-xl p-jade-250 min-500:p-0 min-500:rounded-none">
        <AddressHeader
          formName="shipping-address"
          country={shippingCountry ?? 'BR'}
          hideCepLink
        />
        <ShippingAddressReview
          shippingAddress={shippingAddress}
          editShippingAddress={() => {
            analiticaInstance.events.billingPage.editDeliveryAddressClicked();
            push(`/${params.paymentLinkId}/delivery-address`);
          }}
        />
      </div>
      <div className="hidden min-500:block w-full py-jade-300">
        <Divider direction="horizontal" size="small" />
      </div>
      {showAddress && (
        <>
          <div className="flex flex-col gap-jade-150 bg-jade-background-surface rounded-xl p-jade-250 min-500:p-0 min-500:rounded-none">
            <DeliveryCard
              deliveryDays={deliveryQuote?.sla_working_days}
              deliveryCost={deliveryQuote?.cost}
            />
          </div>
          <div className="hidden min-500:block w-full pb-jade-300 pt-jade-150">
            <Divider direction="horizontal" size="small" />
          </div>
        </>
      )}
      <div className="flex flex-col gap-jade-150 bg-jade-background-surface rounded-xl p-jade-250 mb-jade-200 min-500:p-0 min-500:rounded-none min-500:mb-0">
        <AddressHeader
          formName="billing-address"
          country={billingCountry ?? 'BR'}
          hideCepLink={sameAddress}
          screen={BILLING_SCREEN}
        />
        <div className="py-4">
          <Checkbox
            id="same-address"
            label={formatMessage({ id: 'addressField.sameAddress' })}
            checked={sameAddress}
            onChange={handleSameAddress}
          />
        </div>
        {!sameAddress && (
          <AddressFormComponent
            formName="billing-address"
            screen={BILLING_SCREEN}
          />
        )}
      </div>
      {!sameAddress && (
        <div className="hidden min-500:block w-full py-2">
          <Divider direction="horizontal" size="small" />
        </div>
      )}
      {sameAddress && (
        <div className="hidden min-500:block w-full pb-jade-300 pt-jade-150">
          <Divider direction="horizontal" size="small" />
        </div>
      )}
    </div>
  );
}
